import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { InsertChildRiskOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertChildRiskOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type InsertChildRiskOversightResultInput = {
  OversightAssessmentId: string;
  RiskIds: string[];
  ControlType: RiskAssessmentResultControlType;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertChildRiskOversightResult = createMutationHook({
  name: 'insertChildRiskOversightResult',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.assessment.insertChildRiskOversightResult.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertChildRiskOversightResultInput) => input,
  // Apollo parity: evictField(cache, 'risk_controlled_oversight_result')
  // + 'risk_uncontrolled_oversight_result'
  invalidate: (trpc) => [
    trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
    trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
    trpc.frontend.assessment.oversightResultById.pathKey(),
    trpc.frontend.assessment.riskOversightResultById.pathKey(),
    trpc.frontend.assessment.oversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
    trpc.frontend.assessment.latestOversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
    // Apollo parity: evictField(cache, '*_oversight_result_aggregate')
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    // Apollo parity: evictField(cache, 'oversight_assessment')
    trpc.frontend.assessment.oversightActivityRegister.queryKey(),
    trpc.frontend.assessment.oversightActivityCount.queryKey(),
  ],
  mapResponse: (result: {
    Ids: string[];
  }): InsertChildRiskOversightResultMutation => ({
    insertChildRiskOversightResult: {
      __typename: 'InsertChildRiskAssessmentResultOutput',
      Ids: result.Ids,
    },
  }),
  graphqlDocument: InsertChildRiskOversightResultDocument,
  graphqlVariables: (input: InsertChildRiskOversightResultInput) => input,
  evictFields: [
    'risk_controlled_oversight_result',
    'risk_uncontrolled_oversight_result',
    'oversight_assessment',
    'risk_controlled_oversight_result_aggregate',
    'risk_uncontrolled_oversight_result_aggregate',
  ],
});
